import { Abi, Address, Hex } from 'viem';
import {
  getP2PContract,
  IEX_TYPES,
  prepareOrder,
  PrepareOrderArgs,
} from '@sdk/orderExecutor/helpers';
import { makeOrderMethod, orderSigChain } from '@sdk/orderExecutor/helpers/order';
import OptimisticP2P_Mainnet from '@sdk/shared/lib/abis/OptimisticP2P_Mainnet';

import {
  ensureCorrectNetwork,
  filterAbiForSpecificFunc,
  signOrder,
  signOrderMethod,
} from '@sdk/orderExecutor/functions';
import { getErrorMessage, getTradeType, verifyTypedDataSig } from '@sdk/utils';
import {
  OrderResponse,
  OrderType,
  OrderTypeKey,
  ContractOrderMethod,
  ContractOrderMethodKey,
  OrderMethodPayload,
  QuickTrade,
  CreateOrderResponse,
  OrderUpdate,
  OrderPayload,
} from '@sdk/shared/types/order.types';
import Erc20Permit from '@sdk/shared/lib/abis/Erc20Permit';
import Erc20 from '@sdk/shared/lib/abis/Erc20';
import OptimisticP2P from '@sdk/shared/lib/abis/OptimisticP2p';
import type { Auth } from '@sdk/auth';
import { ExtendedWalletClient } from '@sdk/auth/types';
import { EnrichedAd as AdsEnrichedAd } from '@sdk/shared/types/ads.types';
import { IxEvent, IxEventType } from '@sdk/notification/types';
import { IS_SPONSORSHIP_ENABLED } from '@sdk/shared/constants';
import {
  AuthorisationError,
  BaseError,
  IncompatibleNetworkError,
  IxError,
  KYCError,
} from '@sdk/errors';

import signPermit from '@sdk/orderExecutor/helpers/signPermit';
import { SdkClient } from '@sdk/index';
import { readContract } from '@shared/src/components/utils/blockchain';
import { checkIfGasSponsored } from '@shared/src/lib/functions';

export type ExecuteOrderResult<T = CreateOrderResponse | OrderUpdate | null | undefined> = Promise<
  ExecuteSuccess<T> | ExecuteError
>;

type ExecuteSuccess<T = any> = {
  data: T;
  error?: IxError | null;
};

type ExecuteError = {
  data?: null;
  error: BaseError | null;
};

// Define a simple Chain type and chains array
interface Chain {
  id: number;
  name: string;
}

export type OrderExecutorArgs =
  | CreateOrderProps
  | ExecuteOrderProps
  | acceptQuickTradeByMerchantProps;

interface BaseOrderExecutorProps {
  paymentConfig?: number;
  paymentMethod?: string;
}

interface CreateOrderProps extends BaseOrderExecutorProps {
  action: 'createOrder';
  amount: number;
  ad: AdsEnrichedAd;
  orderData: undefined;
  paymentMethod: string;
}

interface acceptQuickTradeByMerchantProps extends BaseOrderExecutorProps {
  action: 'acceptQuickTradeByMerchant';
  quickTrade: QuickTrade;
  ad: AdsEnrichedAd;
  amount: undefined;
  orderData: undefined;
}

interface ExecuteOrderProps extends BaseOrderExecutorProps {
  action: Exclude<ContractOrderMethodKey, 'createOrder' | 'acceptQuickTradeByMerchant'>;
  orderData: OrderResponse;
  ad?: undefined;
  amount?: undefined;
}

interface WriteContractParams {
  abi: Abi;
  address: Address;
  functionName: string;
  args: readonly unknown[];
  waitForReceipt?: boolean;
  gasLimit?: bigint;
}

export async function writeContractWithSimulation(
  client: ExtendedWalletClient,
  account: Address,
  { abi, address, functionName, args, waitForReceipt = false, gasLimit }: WriteContractParams,
): Promise<{ hash: string; receipt?: unknown }> {
  const { request } = await client.simulateContract({
    account,
    address,
    abi,
    functionName,
    args,
  });

  if (!client.account) {
    throw new Error('Account not found');
  }

  const hash = await client.writeContract({
    account: client.account,
    address,
    abi,
    functionName,
    args,
    chain: client.chain,
  });
  let receipt: unknown = undefined;
  if (waitForReceipt) {
    receipt = await client.waitForTransactionReceipt({ hash });
  }
  return { hash, receipt };
}
export type OrderExecutorOptions = {
  onMessage?: (message: IxEvent) => void;
  sponsorGas?: boolean;
  contactWriter?: (params: WriteContractParams) => Promise<{ hash: Hex; receipt?: unknown }>;
};

export async function executeOrder(
  sdk: SdkClient,
  args: OrderExecutorArgs,
  options: OrderExecutorOptions = {},
): ExecuteOrderResult {
  /* -------------------------------------------------------------------------- */
  /*                               0. CONTEXT SETUP                             */
  /* -------------------------------------------------------------------------- */
  const { env, auth } = sdk;
  const walletClientNullable = auth.getWalletClient();
  if (!walletClientNullable) {
    throw new Error('WalletClient must be set in SDK before executing orders.');
  }
  const walletClient = walletClientNullable as ExtendedWalletClient;
  const addressInitial = auth.getAddress();
  if (!walletClient || !addressInitial) {
    throw new Error('Adress must be set in SDK walletclient before executing orders.');
  }
  const address = addressInitial;

  const chains = auth.chains;
  const isMainnet = env === 'mainnet';
  const kycStatus = auth.kyc?.kycStatus;
  const { onMessage, sponsorGas = true } = options;
  const activeChain = sdk.auth.getActiveChain();
  if (!activeChain) {
    console.error({
      activeChain,
      sdk,
    });
    throw new Error('Active chain not found');
  }
  const activeChainId = activeChain?.id;

  const { amount, ad, orderData, paymentMethod, action } = args;
  const quickTrade = action === 'acceptQuickTradeByMerchant' ? args.quickTrade : undefined;
  const isMerchant =
    address.toLowerCase() === orderData?.merchant.address.toLowerCase() ||
    address.toLowerCase() === ad?.merchantAddress.toLowerCase();

  /* -------------------------------------------------------------------------- */
  /*                               1. LOG HELPERS                               */
  /* -------------------------------------------------------------------------- */
  type LogPayload = {
    type: string;
    message: string;
    title?: string;
    description?: string;
  };

  const logMessage = (payload: LogPayload): void => {
    if (onMessage) {
      onMessage({
        type: payload.type as unknown as IxEventType,
        data: payload,
      } as unknown as IxEvent);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                           2. PRE-OP VALIDATIONS                            */
  /* -------------------------------------------------------------------------- */
  if (auth.status === 'unauthenticated') {
    return { error: new AuthorisationError('Please sign in to continue') };
  }

  if (isMainnet && kycStatus !== 'approved') {
    logMessage({
      type: 'warning',
      title: 'KYC Verification Required',
      message: 'KYC verification required to proceed on mainnet.',
    });
    return { error: new KYCError() };
  }

  if (!orderData && !ad) {
    throw new Error('Order Data or Ad is required');
  }

  /* -------------------------------------------------------------------------- */
  /*                         3. SHARED UTILITY HELPERS                          */
  /* -------------------------------------------------------------------------- */
  const writeContractImpl = (params: WriteContractParams) =>
    options.contactWriter
      ? options.contactWriter(params)
      : writeContractWithSimulation(walletClient, address, params);

  // const methodExpiry = Math.floor(Date.now() / 1000) + 60 * 15; // 15 minutes
  const methodExpiry = 17517789980 + 60 * 30; //

  const buildPrepareArgs = (): PrepareOrderArgs => {
    const isCreate = action === 'createOrder';
    const isAcceptQuick = action === 'acceptQuickTradeByMerchant';
    const isCreateOrQuick = isCreate || isAcceptQuick;

    const selectedType = isCreate ? ad!.type : isAcceptQuick ? quickTrade!.type : orderData!.type;

    return {
      trader: isCreate ? address : isAcceptQuick ? quickTrade!.trader : orderData!.trader.address,
      merchant: isCreate
        ? ad!.merchant!.address
        : isAcceptQuick
        ? address
        : orderData!.merchant.address,
      token: isCreateOrQuick ? ad!.tokenDetails.address : orderData!.token.address,
      currency: isCreateOrQuick ? ad!.currencyId : orderData!.currency.id,
      paymentMethod: isAcceptQuick
        ? quickTrade!.paymentMethod
        : paymentMethod || orderData!.paymentMethod.id,
      quantity: isCreate
        ? Number(amount)
        : isAcceptQuick
        ? quickTrade!.quantity
        : orderData!.quantity,
      orderType: OrderType[selectedType as OrderTypeKey],
      traderChain: isCreate
        ? activeChainId
        : isAcceptQuick
        ? quickTrade?.traderChain || activeChainId
        : orderData!.traderNetwork.id,
      merchantChain: isCreateOrQuick ? ad!.networkId : orderData!.traderNetwork.id,
      duration: isCreateOrQuick ? ad!.timeLimit : orderData!.duration,
      paymentConfig: isCreateOrQuick ? args.paymentConfig : orderData!.paymentConfig?.id,
      userAddress: address,
      activeChainId,
      activeChain,
      tokenDecimals: isCreateOrQuick ? ad!.tokenDetails.decimals : orderData!.token.decimals,
      expiry: isCreateOrQuick ? Math.floor(Date.now() / 1000) + 60 * 60 : orderData!.expiry,
    };
  };
  const prepareArgs = buildPrepareArgs();
  console.log('Prepared Args', prepareArgs, args, orderData);

  /* ------------------------------ NETWORK CHECK ------------------------------ */
  /*                                                                             */
  /* --------------------------------------------------------------------------- */

  if (!sdk.config.networks) throw new Error('Networks not found');
  const { p2pContract } = await ensureCorrectNetwork({
    networks: sdk.config.networks,
    traderChain: prepareArgs.traderChain,
    merchantChain: prepareArgs.merchantChain,
    orderType: prepareArgs.orderType,
    activeChainId,
  });
  const sigchainId = prepareArgs.merchantChain;
  const isCorrectNetwork = activeChainId === sigchainId;

  if (!isCorrectNetwork) {
    logMessage({
      type: 'info',
      title: 'Switch Network',
      message: `Please switch to ${sigchainId} to continue`,
      description: `This operation requires ${sigchainId} network`,
    });
    return {
      error: new IncompatibleNetworkError('Incompatible Network', {
        cause: null,
        data: {
          traderChainId: prepareArgs.traderChain,
          merchantChainId: prepareArgs.merchantChain,
          activeChainId,
          expectedChainId: sigchainId,
          expectedChainName: chains.find(c => c.id === sigchainId)?.name,
        },
      }),
      data: null,
    };
  }

  /* ----------------------------`---------------------------------------------- */
  /*                           4. CORE PREPARATIONS                             */
  /* -------------------------------------------------------------------------- */
  const { madeOrder, orderHash, orderArgs, domain } = await prepareOrder(
    prepareArgs,
    p2pContract,
    sigchainId,
  );

  const shouldAlwaysUseSignatures: boolean = (() => {
    const isEligibleForSponsorship = checkIfGasSponsored({
      sdk,
      chainId: activeChainId,
      destinationAddress: p2pContract,
      connectorId: 'other',
      functionName: action === 'createOrder' ? 'createOrder' : action,
    });
    if (sponsorGas && isEligibleForSponsorship) {
      return true;
    } else {
      return false;
    }
    // return action === 'acceptOrder' && !orderData?.traderSignature; TODO: @Jovells REVIEW
  })();

  /* ------------------------------- SELL CHECKS ------------------------------- */
  async function performSellChecks(): Promise<void> {
    const tokenAddress = prepareArgs.token as Address;

    const allowance = (await readContract({
      walletClient,
      chain: activeChain!,
      args: {
        address: tokenAddress,
        abi: Erc20Permit as Abi,
        functionName: 'allowance',
        args: [address, p2pContract],
      },
    })) as bigint;

    const balance = (await readContract({
      walletClient,
      chain: activeChain!,
      args: {
        address: tokenAddress,
        abi: Erc20Permit as Abi,
        functionName: 'balanceOf',
        args: [address],
      },
    })) as bigint;

    const quantity = BigInt(madeOrder.quantity);

    if (balance < quantity) throw 'Insufficient Balance';

    if (allowance < quantity) {
      try {
        if (!activeChain) throw 'Active chain not found';
        const permitSig = await signPermit({
          spender: p2pContract,
          deadline: BigInt(madeOrder.expiry),
          chain: activeChain,
          tokenAddress,
          walletClient,
          owner: address,
          value: quantity,
        });

        await writeContractImpl({
          abi: Erc20Permit as Abi,
          address: tokenAddress,
          functionName: 'permit',
          args: [
            address,
            p2pContract,
            quantity,
            madeOrder.expiry,
            permitSig.v,
            permitSig.r,
            permitSig.s,
          ],
          waitForReceipt: true,
        });
      } catch (e) {
        const res = await writeContractImpl({
          abi: Erc20 as Abi,
          address: tokenAddress,
          functionName: 'approve',
          args: [p2pContract, quantity],
          waitForReceipt: true,
        });
      }
      logMessage({ type: 'success', message: 'Allowance Created Successfully' });
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                              5. ACTIONS                                    */
  /* -------------------------------------------------------------------------- */
  async function handleAppealOrder(): ExecuteOrderResult {
    logMessage({ type: 'info', title: 'Appeal Placed', message: getSuccessMessage('appealOrder') });

    return { data: null };
  }

  async function handleCreateOrder(): ExecuteOrderResult {
    if (!ad?.id) throw 'Ad Id is required';

    const { tradeType, isMerchant: isUserMerchant } = getTradeType(
      ad.type,
      ad.merchantAddress,
      address,
    );
    if (isUserMerchant) throw 'You cannot trade with yourself';

    const sig = shouldAlwaysUseSignatures ? await signOrder(sdk, madeOrder, domain) : undefined;
    if (!activeChain) throw new Error('Active chain not found');
    const otherSig = '0x';
    const traderSig = !isUserMerchant ? sig : otherSig;
    const merchantSig = isUserMerchant ? sig : otherSig;

    if (tradeType === 'sell') {
      await performSellChecks();
      await writeContractImpl({
        abi: shouldAlwaysUseSignatures
          ? OptimisticP2P_Mainnet
          : filterAbiForSpecificFunc('createOrder', 1, OptimisticP2P_Mainnet),
        address: p2pContract,
        functionName: 'createOrder',
        args: shouldAlwaysUseSignatures ? [madeOrder, traderSig, merchantSig] : [madeOrder],
        waitForReceipt: true,
      });
      logMessage({ type: 'success', message: 'Order Created Successfully IN CONTRACT' });
    } else if (tradeType === 'buy' && !shouldAlwaysUseSignatures) {
      await writeContractImpl({
        abi: filterAbiForSpecificFunc('createOrder', 1, OptimisticP2P_Mainnet),
        address: p2pContract,
        functionName: 'createOrder',
        args: [madeOrder],
        waitForReceipt: true,
      });
    }

    const orderPayload: OrderPayload = { order: orderArgs, adId: ad.id, signature: traderSig };
    if (tradeType === 'sell') orderPayload.paymentConfig = prepareArgs.paymentConfig;

    const res = await sdk.orders.createOrder(orderPayload);
    if (tradeType === 'buy' && res.data) {
      logMessage({ type: 'success', message: getSuccessMessage('createOrder')! });
    }

    return res;
  }

  async function handleacceptQuickTradeByMerchant(): ExecuteOrderResult {
    if (!ad?.id) throw 'Ad Id is required';
    if (!quickTrade?.id) throw 'Quick Trade Id is required';

    const sig: `0x${string}` = (await signOrder(sdk, madeOrder, domain)) as `0x${string}`;

    const orderPayload: OrderPayload = {
      order: orderArgs,
      adId: ad.id,
      signature: sig,
      paymentConfig: quickTrade.paymentConfig,
    };

    console.log('Accepting quick trade', { quickTrade });

    if (quickTrade.type === 'buy') await performSellChecks();
    const res = await sdk.orders.acceptQuickTradeByMerchant({
      quickTradeId: quickTrade.id,
      orderSig: orderPayload,
    });

    logMessage({ type: 'success', message: 'Quick Trade accepted successfully' });
    return res;
  }

  async function handleAcceptOrder(): ExecuteOrderResult {
    if (!orderData) throw 'Order Data is required';
    const isQuickTrade = orderData.quickTradeIndex !== null;

    const sig: `0x${string}` = shouldAlwaysUseSignatures
      ? await signOrder(sdk, madeOrder, domain)
      : '0x';

    const merchantSig: `0x${string}` = isQuickTrade ? orderData.merchantSignature || '0x' : sig;
    const traderSig: `0x${string}` | null = isQuickTrade ? sig : orderData.traderSignature;
    if (orderHash !== orderData.id) throw 'Order hash mismatch';

    const { tradeType } = getTradeType(orderData.type, orderData.merchant.address, address);

    if (tradeType === 'sell') {
      if (!prepareArgs.paymentConfig) throw 'Payment config is required to accept sell orders';
      await performSellChecks();
    }

    try {
      if (!traderSig) {
        await writeContractImpl({
          abi: filterAbiForSpecificFunc('acceptOrder', 1, OptimisticP2P_Mainnet),
          address: p2pContract,
          functionName: 'acceptOrder',
          args: [madeOrder],
          waitForReceipt: true,
        });
      } else {
        await writeContractImpl({
          abi: OptimisticP2P_Mainnet,
          address: p2pContract,
          functionName: 'createOrder',
          args: [madeOrder, traderSig, merchantSig],
          waitForReceipt: true,
        });
      }
      logMessage({ type: 'success', message: 'Order Created Successfully IN CONTRACT' });
    } catch (e: any) {
      if (e.message.includes('OrderExists()')) {
        console.warn('order already exists, skipping contract write');
      } else {
        throw e;
      }
    }

    const res = await sdk.orders.acceptOrder(orderData.id, {
      signature: sig,
      methodPayload: orderHash,
      paymentConfig: prepareArgs.paymentConfig,
    });
    logMessage({ type: 'success', message: getSuccessMessage('acceptOrder')! });
    return res;
  }

  async function handleOrderMethod(
    methodName: 'payOrder' | 'releaseOrder' | 'cancelOrder',
  ): ExecuteOrderResult {
    if (!orderData) throw 'Order Data is required';
    if (orderHash !== orderData.id) throw 'Order hash mismatch';
    const isQuicktradeCancel = orderData.state === 'intent' && orderData.quickTradeIndex;

    const shouldSign = !isQuicktradeCancel && shouldAlwaysUseSignatures;

    const methodPayload: OrderMethodPayload = {
      orderHash,
      method: ContractOrderMethod[methodName as ContractOrderMethodKey],
      expiry: methodExpiry,
    };

    const orderMethodForContract = shouldSign ? makeOrderMethod(methodPayload) : null;
    let methodSig: `0x${string}` | undefined;
    if (orderMethodForContract) {
      methodSig = await signOrderMethod(walletClient, orderMethodForContract, domain);
    }

    const contractArgs = {
      address: p2pContract,
      abi: shouldSign
        ? OptimisticP2P
        : filterAbiForSpecificFunc(methodName, 1, OptimisticP2P_Mainnet),
      functionName: methodName,
      args: shouldSign
        ? [
            {
              orderHash,
              method: ContractOrderMethod[args.action],
              expiry: BigInt(methodExpiry),
            },
            methodSig,
          ]
        : [args.orderData.id],
    };

    const types = {
      OrderMethodPayload: [
        { name: 'orderHash', type: 'bytes32' },
        { name: 'method', type: 'uint8' },
        { name: 'expiry', type: 'uint256' },
      ],
    } as const;

    // const isValid = await verifyTypedDataSig({
    //   address: address as `0x${string}`,
    //   domain,
    //   types,
    //   primaryType: 'OrderMethodPayload',
    //   message: methodPayload,
    //   signature: methodSig as `0x${string}`,
    //   chain: chains.find(c => c.id === activeChainId)!,
    // });

    if (!isQuicktradeCancel) await writeContractImpl(contractArgs);

    const res = await sdk.orders.updateOrder(orderData.id, {
      signature: methodSig,
      methodPayload,
    });

    logMessage({
      type: 'success',
      message: getSuccessMessage(methodName),
    });
    return res;
  }

  /* -------------------------------------------------------------------------- */
  /*                                 6. FLOW                                    */
  /* -------------------------------------------------------------------------- */
  logMessage({ type: 'loading', message: 'Processing' });

  try {
    switch (action) {
      case 'appealOrder':
        return await handleAppealOrder();
      case 'createOrder':
        return await handleCreateOrder();
      case 'acceptQuickTradeByMerchant':
        return await handleacceptQuickTradeByMerchant();
      case 'acceptOrder':
        return await handleAcceptOrder();
      case 'payOrder':
      case 'releaseOrder':
      case 'cancelOrder':
        return await handleOrderMethod(action);
      default:
        throw new Error(`Unsupported action: ${action}`);
    }
  } catch (error: unknown) {
    const errorMessage = getErrorMessage(error);
    logMessage({
      type: 'error',
      title: (error as Error)?.name ?? 'An error occurred',
      message: errorMessage,
    });
    return { error: new BaseError<Record<string, unknown>>(errorMessage || 'An error occurred') };
  }
}

export type {
  OrderResponse,
  OrderType,
  OrderTypeKey,
  ContractOrderMethod as OrderMethod,
  ContractOrderMethodKey as OrderMethodKey,
  OrderMethodPayload,
  PrepareOrderArgs,
  Chain,
};

// ---------------------------------------------------------------------------
// Helper: map action -> default success message
// ---------------------------------------------------------------------------

function getSuccessMessage(action: OrderExecutorArgs['action']): string {
  switch (action) {
    case 'acceptOrder':
      return 'Order Accepted Successfully';
    case 'cancelOrder':
      return 'Order Cancelled Successfully';
    case 'payOrder':
      return 'Order Paid Successfully';
    case 'releaseOrder':
      return 'Order Released Successfully';
    case 'appealOrder':
      return 'The appeal function is currently handled off-chain. Please join our support center on Telegram and provide your order details: https://t.me/+wLyQQTNHwM9jNmE8';
    default:
      return 'Action completed successfully';
  }
}
